<template>
   <b-container v-if="loaded" class="get-started-container">
      <!-- Section intro -- only show once -->
      <div v-if="!seenIntro || page === 0">
         <ManagerMessage key="welcome-mm" :value="true">
            <template v-if="manager" #short
               >You can expand and collapse my messages to you.</template
            >

            <h2 v-if="manager" class="welcome-title">Welcome, {{ profile.firstName }}!</h2>

            <p v-if="manager" class="mb-0 welcome-description">
               I'm <b>{{ manager.firstName }} {{ manager.lastName }}</b
               >, your {{ managerRole }} at <a href="https://rdig.com/">RDIG</a>. I'm here to guide
               you through providing the info we need to calculate and substantiate your R&D credit.
               Look for help in messages like these as you use Onboard. And if you have any
               questions, please don't hesitate to contact me and the rest of your credit study team
               using the secure messaging feature in the top right corner.
            </p>

            <b-row class="mt-4">
               <b-col
                  cols="12"
                  md="6"
                  class="text-center d-flex flex-column align-items-center justify-content-center"
               >
                  <div class="welcome-img welcome-img-1"></div>
               </b-col>
               <b-col cols="12" md="6">
                  <div class="welcome-img welcome-img-2"></div>
               </b-col>
            </b-row>

            <b-row class="mb-5 mt-4">
               <b-col cols="12" md="6" order="1" order-md="1">
                  <h1 class="section-title">About the R&amp;D Credit</h1>
                  <p class="section-content">
                     Research and Development (R&D) tax credits are permanent U.S. federal and state
                     tax incentives designed to reward innovative companies for developing or
                     improving products and/or processes within U.S. territory. The credit is based
                     on the amount of
                     <strong>Qualified Research Expenditures (QREs)</strong> incurred by your
                     company's R&amp;D initiatives in four groups:
                     <strong
                        >employee wages, materials/supplies costs, contract research costs,</strong
                     >
                     and <strong>cloud computing costs.</strong>
                  </p>
               </b-col>
               <b-col
                  cols="12"
                  md="6"
                  order="1"
                  order-md="2"
                  class="d-flex flex-column align-items-start"
               >
                  <h1 class="section-title">Making it Simple for You</h1>
                  <p class="section-content">
                     We are your R&D credit team, engaged by your company to calculate its R&D tax
                     credit. That process starts with collecting information about your qualified
                     expenses, and that's why we built Onboard <em>for you</em>. Once your company
                     has provided all the info we need, we will apply our expertise and decades of
                     experience to ensure your company receives the tax credit you're due, and will
                     support you in the event of any audit. Let's begin!
                  </p>
               </b-col>
            </b-row>

            <div class="d-flex justify-content-end mt-4">
               <div>
                  <b-button
                     id="btn-begin"
                     class="action-button begin-button"
                     variant="success"
                     @click="leaveIntro"
                  >
                     Let's Begin
                     <b-icon-arrow-right-short />
                  </b-button>
               </div>
            </div>
         </ManagerMessage>
      </div>

      <div v-else>
         <ManagerMessage key="4pt-mm">
            <h2 class="introduction-title">Introduction: The Four-Part Test</h2>

            <p class="introduction-description">
               Congress has defined a test to help you determine what expenses qualify for R&amp;D
               tax credits. Please keep this test in mind as you tell us how much of your company's
               spending qualifies. All four parts of this test must be satisfied for a project or
               initiative to qualify.
            </p>
            <p class="introduction-description pb-4">
               Let's briefly go through each part of the test:
            </p>

            <GetStartedProgress :stage="page" @stepChanged="onStepChanged"></GetStartedProgress>
            <GetStartedStageInfo :stage="page"></GetStartedStageInfo>

            <div class="d-flex justify-content-between mt-4">
               <div>
                  <b-button
                     variant="primary"
                     class="action-button dark-button introduction-button"
                     size=""
                     @click="prevPage"
                     v-if="page === 1"
                  >
                     <b-icon-arrow-left-short />
                     View Introduction
                  </b-button>
                  <b-button
                     variant="primary"
                     class="action-button dark-button back-button"
                     size=""
                     @click="prevPage"
                     v-else-if="page > 1"
                  >
                     <b-icon-arrow-left-short />
                     Back
                  </b-button>
               </div>
               <b-button
                  id="btn-next"
                  class="action-button"
                  :class="{'next-button': page !== 5, 'continue-button': page === 5}"
                  :variant="page === 5 ? 'success' : 'primary'"
                  size=""
                  @click="nextPage"
               >
                  {{ nextButtonText }}
                  <b-icon-arrow-right-short />
               </b-button>
            </div>
         </ManagerMessage>
      </div>
   </b-container>
</template>

<script>
import {mapGetters} from 'vuex';
import {SidebarStates} from '@/store/modules/session';

import GetStartedProgress from '@/views/stages/widgets/GetStartedProgress.vue';
import GetStartedStageInfo from '@/views/stages/widgets/GetStartedStageInfo.vue';

export default {
   components: {
      GetStartedStageInfo,
      GetStartedProgress,
   },

   data() {
      return {
         SidebarStates,
         loaded: false,
         page: 1,
      };
   },

   computed: {
      ...mapGetters({
         clientState: 'clientState',
         profile: 'profile',
         manager: 'manager',
         greeted: 'greeted',
         isAppConfigured: 'isAppConfigured',
         isCustomer: 'isCustomer',
         isSME: 'isSME',
         priorityActionItems: 'progress/priorityActionItems',
         isProfileQuestionnaireComplete: 'progress/isProfileQuestionnaireComplete',
      }),

      companyId() {
         return this.profile.companyId;
      },

      managerRole() {
         if (this.manager === null) {
            return '';
         }
         switch (this.manager.role) {
            case 'PM':
               return 'Project Manager';
            case 'BM':
               return 'Business Manager';
            case 'PA':
               return 'Project Associate';
            default:
               return 'Project Manager';
         }
      },

      /** Has the user seen the intro for this section? */
      seenIntro() {
         return this.clientState.sectionIntros.whatQualifies;
      },

      nextButtonText() {
         if (this.page === 5) {
            if (this.isAppConfigured) {
               return 'Continue to Dashboard';
            }
            return 'Continue';
         }
         return 'Next';
      },
   },

   methods: {
      gotoPage(pageNum) {
         this.page = pageNum;
      },

      leaveIntro() {
         if (!this.seenIntro) {
            this.$store.dispatch('setSectionIntro', {
               section: 'whatQualifies',
               value: true,
            });
         }
         this.gotoPage(1);
      },

      /** Select the next page, or take the user to the next thing they need to do */
      async nextPage() {
         if (this.page === 5) {
            if (!this.greeted) {
               await this.$store.dispatch('returnGreeting', {value: true});
               this.$store.commit('setSidebar', {sidebar: SidebarStates.EXPANDED});
            }
            this.$router.push({name: this.isAppConfigured ? 'Home' : 'study-setup'});
         } else {
            this.gotoPage(this.page + 1);
         }
      },

      prevPage() {
         this.gotoPage(this.page - 1);
      },

      onStepChanged(nextPage) {
         this.gotoPage(nextPage);
      },
   },

   async created() {
      try {
         if (this.isSME) {
            await this.loadPage([
               this.$store.dispatch('progress/loadProgress', {companyId: this.companyId}),
            ]);
         }
      } finally {
         this.loaded = true;
      }
      // Keeping this around for debuging purposes. Uncomment this to set the user back to the ungreeted state
      // await this.$store.dispatch('returnGreeting', {value: false});
   },
};
</script>

<style lang="scss" scoped>
.welcome-logo {
   display: inline;
   height: 76px;
   margin-left: -16px;
   margin-right: -18px;
}

.welcome-title {
   font-size: 1.5rem;
   font-weight: 700;
}

.welcome-description {
   font-size: 0.8125rem;
   font-weight: 500;
}

.section-title {
   font-size: 1.25rem;
   font-weight: 700;
   color: $secondary;
}

.section-content {
   font-size: 0.8125rem;
   font-weight: 500;
   color: $secondary;
}

.introduction-title {
   font-size: 1.25rem;
   font-weight: 700;
}

.introduction-description {
   font-size: 0.8125rem;
   font-weight: 500;
}

.action-button {
   font-size: 0.8125rem;
}

.begin-button {
   width: 8.75rem;
}

.introduction-button {
   width: 10.625rem;
}

.back-button {
   width: 8.75rem;
}

.next-button {
   width: 8.75rem;
}

.continue-button {
   width: 13.125rem;
}

.welcome-img {
   min-height: 320px;

   @include md-max {
      min-height: 240px;
   }

   &.welcome-img-1 {
      @include stylized-image('../../assets/img/team-color.jpg', $pos-y: 20%);
   }

   &.welcome-img-2 {
      @include stylized-image('../../assets/img/accountant.png');

      @include md-max {
         @include stylized-image('../../assets/img/accountant.png', $pos-y: 15%);
      }
   }
}
</style>
